/**
 * Kiosk receipt-style ticket printer.
 *
 * Renders an 80mm receipt-shaped ticket in a hidden iframe and triggers print.
 * If `window.kioskPrinter` is provided (e.g. native bridge from a kiosk app),
 * delegates to it instead.
 */
const COPY = {
  vi: {
    powered_by: "Powered by AsterPOS Corp",
    motto: "Kết Nối • Học Hỏi • Phát Triển",
    event: "Sự kiện",
    date: "Ngày",
    location: "Địa điểm",
    business: "Tiệm",
    owner: "Chủ tiệm",
    phone: "SĐT",
    ticket: "Vé",
    checkin_at: "Check-in",
  },
  en: {
    powered_by: "Powered by AsterPOS Corp",
    motto: "Connect • Learn • Grow",
    event: "Event",
    date: "Date",
    location: "Location",
    business: "Business",
    owner: "Owner",
    phone: "Phone",
    ticket: "Ticket",
    checkin_at: "Checked-in",
  },
};

function buildTicketHtml({ event, ticket, lang = "vi" }) {
  const c = COPY[lang] || COPY.vi;
  const phoneMasked = ticket?.phone_last4 ? `***-${ticket.phone_last4}` : "";
  const checkedAt = ticket?.checked_in_at ? new Date(ticket.checked_in_at).toLocaleString() : "";

  return `<!doctype html>
<html lang="${lang}"><head>
<meta charset="utf-8" />
<title>NVC Ticket ${ticket?.number || ""}</title>
<style>
  @page { size: 80mm auto; margin: 4mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Outfit', 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 8mm 4mm; color: #111; font-size: 11pt; }
  .nvc-logo { font-family: 'Playfair Display', Georgia, serif; font-weight: 700; font-style: italic; font-size: 28pt; text-align: center; letter-spacing: 0.04em; }
  .brand { text-align: center; letter-spacing: 0.22em; font-size: 9pt; font-weight: 600; margin-top: -2pt; }
  .motto { text-align: center; letter-spacing: 0.18em; font-size: 7.5pt; color: #555; margin-top: 4pt; }
  .powered { text-align: center; font-size: 7pt; letter-spacing: 0.22em; color: #777; margin-top: 4pt; text-transform: uppercase; }
  hr { border: none; border-top: 1px dashed #999; margin: 8pt 0; }
  .row { display: flex; justify-content: space-between; padding: 2pt 0; gap: 8pt; }
  .row-label { font-weight: 600; color: #444; }
  .row-value { text-align: right; }
  .event-title { font-family: 'Playfair Display', Georgia, serif; font-weight: 700; font-size: 14pt; text-align: center; margin: 4pt 0; }
  .ticket-num { text-align: center; margin-top: 8pt; padding: 8pt; border: 2px dashed #C9A14A; border-radius: 8pt; font-family: 'Outfit', monospace; font-size: 16pt; letter-spacing: 0.08em; font-weight: 700; }
  .ticket-label { font-size: 7pt; letter-spacing: 0.32em; color: #666; font-weight: 600; }
  .footer { text-align: center; margin-top: 10pt; font-size: 7.5pt; color: #666; letter-spacing: 0.12em; }
</style>
</head><body>
  <div class="nvc-logo">NVC</div>
  <div class="brand">NAIL VIET CLUB</div>
  <div class="motto">${c.motto}</div>
  <div class="powered">${c.powered_by}</div>
  <hr/>
  <div class="event-title">${escapeHtml(event?.name || "")}</div>
  <div class="row"><span class="row-label">${c.date}</span><span class="row-value">${escapeHtml(event?.event_date || "")}</span></div>
  <div class="row"><span class="row-label">${c.location}</span><span class="row-value">${escapeHtml([event?.venue_name, event?.location].filter(Boolean).join(" — ") || event?.location || "")}</span></div>
  <hr/>
  <div class="row"><span class="row-label">${c.business}</span><span class="row-value">${escapeHtml(ticket?.business_name || "")}</span></div>
  ${ticket?.owner_name ? `<div class="row"><span class="row-label">${c.owner}</span><span class="row-value">${escapeHtml(ticket.owner_name)}</span></div>` : ""}
  <div class="row"><span class="row-label">${c.phone}</span><span class="row-value">${escapeHtml(phoneMasked)}</span></div>
  <div class="ticket-num">
    <div class="ticket-label">${c.ticket.toUpperCase()}</div>
    ${escapeHtml(ticket?.number || "")}
  </div>
  <div class="row" style="margin-top:6pt"><span class="row-label">${c.checkin_at}</span><span class="row-value">${escapeHtml(checkedAt)}</span></div>
  <hr/>
  <div class="footer">${c.motto}</div>
</body></html>`;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export async function printKioskTicket({ event, ticket, lang = "vi", forceBrowser = false }) {
  const html = buildTicketHtml({ event, ticket, lang });

  // Native bridge (e.g. Electron / Capacitor kiosk shell)
  if (!forceBrowser && typeof window !== "undefined" && window.kioskPrinter && typeof window.kioskPrinter.print === "function") {
    try { await window.kioskPrinter.print(html); return true; }
    catch (_) { /* fall through to browser print */ }
  }

  // Browser print via hidden iframe
  return new Promise((resolve, reject) => {
    try {
      const iframe = document.createElement("iframe");
      iframe.setAttribute("aria-hidden", "true");
      iframe.style.cssText = "position:fixed;width:0;height:0;border:0;left:-9999px;top:-9999px;";
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open(); doc.write(html); doc.close();
      const onPrintReady = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          setTimeout(() => { document.body.removeChild(iframe); resolve(true); }, 1500);
        } catch (e) {
          try { document.body.removeChild(iframe); } catch (_) { /* ignore */ }
          reject(e);
        }
      };
      // Wait for fonts/images briefly
      if (iframe.contentWindow?.document?.readyState === "complete") setTimeout(onPrintReady, 300);
      else iframe.onload = () => setTimeout(onPrintReady, 300);
    } catch (e) {
      reject(e);
    }
  });
}
